'use client';

import { FC, FormEventHandler, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Divider, Flex, LoadingOverlay, Text, Textarea, Title, Tooltip } from '@mantine/core';
import { ProductType } from '@/remoteApi/ProductTypeApi';
import { SlArrowLeft, SlArrowRight, SlEnvolope } from 'react-icons/sl';
import { StepClient } from './StepClient';
import { useDisclosure, useMap, useSessionStorage, useWindowEvent, useWindowScroll } from '@mantine/hooks';
import { StepProduct } from './StepProduct';
import { Attachment, SavedFormState, SessionStorageKey } from '@/types';
import { UUID } from 'crypto';
import { FormTemplate, QuestionType } from '@/remoteApi/types/form-template.types';
import { capitalize } from 'lodash';
import {
	formatDateForPresentation,
	getAllAnswersIds,
	getInitialAnswerData,
	getInitialAnswersMapData,
} from '@/utilities';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { AppPaths } from '@/configs/appPaths';
import { ResetButton } from './ResetButton';
import FormApi, { Form } from '@/remoteApi/FormApi';
import UmamiApi, { UmamiEvent } from '@/remoteApi/UmamiApi';

interface Props {
	productType: ProductType;
	formTemplate: FormTemplate;
	loadState?: boolean;
	onChange?: (formData: any) => void;
	initialAnswersMap?: Map<UUID, FormAnswerMap>;
	initialFormDetails?: {
		quantity: number;
		orderDate: Date | null;
		note: string;
		quantityPerDimension: Form['quantityPerDimension'];
	};
}

export type FormAnswerMap = {
	answerId: UUID | null;
	value: string;
	attachments: Attachment[];
	required: boolean;
};

export type QuantityPerDimensionForm = Form['quantityPerDimension'];
export type UserData = Form['userData'];
export type ShippingData = Form['shippingData'];
export type FormDetails = Pick<Form, 'quantity' | 'orderDate' | 'note'>;
export type FormOptions = {
	minTotalSurface: number;
};

type Step = 'product' | 'client';

export const ProductTypeForm: FC<Props> = ({
	productType,
	formTemplate,
	loadState,
	onChange,
	initialAnswersMap,
	initialFormDetails,
}) => {
	const router = useRouter();

	const [quantityPerDimension, setQuantityPerDimension] =
		useState<QuantityPerDimensionForm>(
			initialFormDetails?.quantityPerDimension || []
		);

	const [userDataForm, setUserDataForm] = useState<UserData>({
		name: '',
		surname: '',
		companyName: '',
		NIP: '',
		city: '',
		postalCode: '',
		address: '',
		email: '',
		phone: '',
	});
	const handleUserDataChange = (
		key: keyof UserData,
		value: UserData[keyof UserData]
	) => {
		setUserDataForm((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const [shippingDataForm, setShippingDataForm] = useState<ShippingData>({
		city: '',
		postalCode: '',
		address: '',
	});
	const handleShippingDataChange = (
		key: keyof ShippingData,
		value: ShippingData[keyof ShippingData]
	) => {
		setShippingDataForm((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const [formOptions, setFormOptions] = useState<FormOptions>({
		minTotalSurface: 0,
	});
	const handleOptionsChange = (
		key: keyof FormOptions,
		value: FormOptions[keyof FormOptions]
	) => {
		setFormOptions((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const today = new Date();
	const maxDate = new Date();
	maxDate.setDate(today.getDate() + 18);

	const [formDetails, setFormDetails] = useState<FormDetails>({
		quantity: initialFormDetails?.quantity || 1,
		orderDate: initialFormDetails?.orderDate || maxDate,
		note: initialFormDetails?.note || '',
	});
	const handleDetailsChange = (
		key: keyof FormDetails,
		value: FormDetails[keyof FormDetails]
	) => {
		setFormDetails((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const answersMap = useMap<UUID, FormAnswerMap>(
		initialAnswersMap
			? Array.from(initialAnswersMap.entries())
			: getInitialAnswersMapData(formTemplate)
	);

	const [isValidating, validationActions] = useDisclosure();
	const [, scrollTo] = useWindowScroll();

	const handleValidation = () => {
		validationActions.open();
		scrollTo({ y: 0 });
	};

	const [step, setStep] = useState<Step>('product');
	useEffect(() => {
		window.scrollTo({
			top: 0,
		});
	}, [step]);
	useEffect(() => {
		if (!onChange) return;

		const currentState = {
			answersMap: new Map(answersMap.entries()),
			...formDetails,
			quantityPerDimension,
		};

		const timeoutId = setTimeout(() => {
			onChange(currentState);
		}, 0);

		return () => clearTimeout(timeoutId);
	}, [
		answersMap.size,
		formDetails.quantity,
		formDetails.orderDate,
		formDetails.note,
		quantityPerDimension.length,
		onChange,
	]);

	const handleReset = () => {
		scrollTo({ y: 0 });
		validationActions.close();

		setStep('product');
		setQuantityPerDimension([]);
		setUserDataForm({
			name: '',
			surname: '',
			companyName: '',
			NIP: '',
			city: '',
			postalCode: '',
			address: '',
			email: '',
			phone: '',
		});
		setShippingDataForm({
			city: '',
			postalCode: '',
			address: '',
		});
		setFormOptions({
			minTotalSurface: 0,
		});
		setFormDetails({
			quantity: 1,
			orderDate: maxDate,
			note: '',
		});

		const initialAnswers = getInitialAnswersMapData(formTemplate);
		answersMap.clear();
		for (const [id, val] of initialAnswers) {
			answersMap.set(id, val);
		}
	};

	const isProductStep = step === 'product';

	const [savedState, setSavedState] = useSessionStorage<SavedFormState | null>({
		key: SessionStorageKey.SAVED_FORM_STATE,
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	useLayoutEffect(() => {
		UmamiApi.emit(UmamiEvent.APP_INIT);

		if (!loadState || !savedState || savedState.productType !== productType.id)
			return;

		setQuantityPerDimension(savedState.quantityPerDimension);
		setUserDataForm(savedState.userDataForm);
		setShippingDataForm(savedState.shippingDataForm);
		setFormDetails({
			...savedState.formDetails,
			orderDate: new Date(savedState.formDetails.orderDate),
		});

		answersMap.clear();

		for (const entry of savedState.answersMap) {
			answersMap.set(entry[0], entry[1]);
		}

		setSavedState(null);
	}, []);

	useWindowEvent('beforeunload', (e) => {
		e.preventDefault();
	});

	const submitMutation = useMutation({
		mutationFn: FormApi.sendForm,
		onError: () => {},
		onSuccess: (form) => {
			const quantitiy = Math.max(
				form.quantity,
				form.quantityPerDimension.reduce((acc, cur) => acc + cur.quantity, 0)
			);

			UmamiApi.emit(UmamiEvent.CONFIGURATION_SUBMITTED, {
				CONFIGURATION_COMPLEXITY: quantitiy,
			});

			router.push(
				`/${AppPaths.PRODUCT_TYPES}/${productType.id}/${AppPaths.FORM}/success?productType=${productType.name}`
			);

			setSavedState({
				productType: productType.id,
				userDataForm,
				shippingDataForm,
				formDetails: {
					...formDetails,
					//@ts-ignore
					orderDate: formDetails.orderDate?.toISOString() || '',
				},
				answersMap: [...answersMap.entries()],
				quantityPerDimension,
			});
		},
	});

	const handleSubmit: FormEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault();

		submitMutation.mutate({
			productType,
			formTemplate,
			userData: userDataForm,
			shippingData: shippingDataForm,
			formDetails,
			answersMap,
			quantityPerDimension,
		});
	};

	const allAnswersIds = getAllAnswersIds(answersMap);
	const filteredQuestionsByRelation = formTemplate.questionGroups.map(
		(group) => ({
			...group,
			questions: group.questions
				.filter(
					(question) =>
						!question.relations?.some(
							(rel) => allAnswersIds.includes(rel.answerId) && !rel.present
						)
				)
				.map((question) => ({
					...question,
					answers: question.answers.filter(
						(answer) =>
							!answer.relations?.some(
								(rel) => allAnswersIds.includes(rel.answerId) && !rel.present
							)
					),
				})),
		})
	);

	const filteredFormTemplate = {
		...formTemplate,
		questionGroups: filteredQuestionsByRelation,
	};

	const filteredProductType = {
		...productType,
		predefinedDimensions: (productType.predefinedDimensions ?? []).filter(
			(dimension) => {
				if (
					dimension.relations &&
					dimension.relations.some(
						(rel) => allAnswersIds.includes(rel.answerId) && !rel.present
					)
				) {
					return false;
				}
				return true;
			}
		),
	};

	const getQuestionById = (questionId: UUID) => {
		return formTemplate.questionGroups
			.flatMap((group) => group.questions)
			.find((question) => question.id === questionId);
	};

	const getAnswerById = (answerId: UUID) => {
		return formTemplate.questionGroups
			.flatMap((group) => group.questions)
			.flatMap((question) => question.answers)
			.find((answer) => answer.id === answerId);
	};

	answersMap.forEach((answerData, questionId) => {
		if (!answerData.answerId) return;

		const answer = getAnswerById(answerData.answerId as UUID);
		const question = getQuestionById(questionId);
		if (!answer || !question) return;

		const relations = answer.relations;

		if (
			relations.some(
				(relation) =>
					allAnswersIds.includes(relation.answerId) && !relation.present
			)
		) {
			answersMap.set(questionId, {
				...getInitialAnswerData(question),
				required: answerData.required,
			});
		}
	});

	const formAnswers = [...answersMap.entries()].map(
		([questionId, currentQuestionFormValue]) => {
			const question = getQuestionById(questionId);
			if (!question) return currentQuestionFormValue;

			const relations = question.relations;

			if (
				relations.some(
					(relation) =>
						allAnswersIds.includes(relation.answerId) && !relation.present
				)
			) {
				return getInitialAnswerData(question);
			}

			return currentQuestionFormValue;
		}
	);

	const handleNextStep = () => {
		validationActions.close();
		setStep('client');
	};

	const isFormCompleted = useMemo(() => {
		const hasQuestionWithDimensions = filteredFormTemplate.questionGroups
			.flatMap((group) => group.questions)
			.some((question) => question.questionType === QuestionType.DIMENSIONS);

		if (
			(productType.predefinedDimensions.length ||
				productType.isPerDimensionQuantityEnabled) &&
			!quantityPerDimension.length &&
			hasQuestionWithDimensions
		)
			return false;

		return !formAnswers.some(
			(answer) =>
				answer.required &&
				!answer.value &&
				!answer.attachments.length &&
				!answer.answerId
		);
	}, [formAnswers, answersMap, quantityPerDimension]);

	return (
		<Flex
			gap="lg"
			component="form"
			align={{
				base: 'stretch',
				lg: 'flex-start',
			}}
			onSubmit={handleSubmit}
			direction={{
				base: 'column',
				lg: 'row',
			}}
		>
			{isProductStep ? (
				<StepProduct
					productType={filteredProductType}
					formTemplate={filteredFormTemplate}
					formDetails={formDetails}
					hanleDetailsChange={handleDetailsChange}
					answersMap={answersMap}
					quantityPerDimension={quantityPerDimension}
					setQuantityPerDimension={setQuantityPerDimension}
					isValidating={isValidating}
					formOptions={formOptions}
					hanldleOptionsChange={handleOptionsChange}
				/>
			) : (
				<StepClient
					userDataForm={userDataForm}
					handleUserDataChange={handleUserDataChange}
					shippingDataForm={shippingDataForm}
					handleShippingDataChange={handleShippingDataChange}
				/>
			)}
			<Box component="aside" w={{ base: '100%', lg: 400 }} pos="sticky" top={0}>
				<LoadingOverlay visible={submitMutation.isPending} />
				<Title order={2} c="black" mb="sm">
					Podsumowanie
				</Title>
				<Card>
					{filteredFormTemplate.questionGroups.map((questionGroup) => (
						<Box key={questionGroup.id}>
							<Title order={3} c="black" mb="md">
								{capitalize(questionGroup.name)}
							</Title>
							{questionGroup.questions
								.filter(
									(question) =>
										question.questionType !== QuestionType.ADVANCED_OPTIONS
								)
								.map((question) => {
									if (question.questionType === QuestionType.ADVANCED_OPTIONS)
										return null;

									if (question.questionType === QuestionType.DIMENSIONS)
										return quantityPerDimension.map((dimension, idx) => (
											<Flex
												key={idx}
												justify="space-between"
												direction={{ base: 'column', lg: 'row' }}
												align="flex-start"
												mb={{ base: 'xl', lg: 'sm' }}
											>
												<Text fw="bold">{`${dimension.depth}mm x ${dimension.width}mm x ${dimension.height}mm`}</Text>
												<Text>{dimension.quantity} szt.</Text>
											</Flex>
										));

									const formAnswer = answersMap.get(question.id) || {
										answerId: null,
										value: '',
										attachments: [],
										required: false,
									};

									let value = '';

									switch (question.questionType) {
										case QuestionType.TEXT:
										case QuestionType.TEXTAREA:
										case QuestionType.NUMBER:
											value = formAnswer.value;
											break;
										case QuestionType.SELECT:
										case QuestionType.MODAL_PICKER: {
											const answer = question.answers.find(
												(answer) => answer.id === formAnswer.answerId
											);
											value = answer?.name || '';
											break;
										}
										case QuestionType.FILES:
											value = formAnswer.attachments
												.map((attachment) => attachment.name)
												.join(', ');
											break;
									}

									if (value) value += question.suffix;

									return (
										<Flex
											key={question.id}
											justify="space-between"
											direction={{ base: 'column', lg: 'row' }}
											align="flex-start"
											mb={{ base: 'xl', lg: 'sm' }}
											gap="sm"
										>
											<Text fw="bold">{question.name}</Text>
											<Text ta={{ lg: 'end' }} maw={{ base: 300, xs: '100%' }}>
												{value || '-'}
											</Text>
										</Flex>
									);
								})}
							<Divider mt="md" mb="sm" />
						</Box>
					))}
					<Title order={3} c="black" mb="md">
						Informacje
					</Title>
					{!filteredProductType.isPerDimensionQuantityEnabled && (
						<Flex
							justify="space-between"
							direction={{ base: 'column', lg: 'row' }}
							align="flex-start"
							mb={{ base: 'xl', lg: 'sm' }}
						>
							<Text fw="bold">Liczba produktów</Text>
							<Text>{formDetails.quantity} szt.</Text>
						</Flex>
					)}
					<Flex
						justify="space-between"
						direction={{ base: 'column', lg: 'row' }}
						align="flex-start"
						mb={{ base: 'xl', lg: 'md' }}
					>
						<Text fw="bold">Termin dostawy</Text>
						<Text>{formatDateForPresentation(formDetails.orderDate)}</Text>
					</Flex>
					<Flex>
						<Textarea
							label="Uwagi"
							readOnly
							placeholder="Brak uwag"
							mb="md"
							autosize
							minRows={4}
							value={formDetails.note}
							w="100%"
						/>
					</Flex>
					<Flex
						gap="md"
						justify="space-between"
						direction={{ base: 'column', lg: 'row' }}
						align="flex-start"
						mb="sm"
					>
						{isProductStep && (
							<Tooltip
								label="Wypełnij wszystkie pola aby przejść dalej"
								disabled={isFormCompleted}
							>
								<Button
									color="blue.8"
									onClick={isFormCompleted ? handleNextStep : handleValidation}
									style={{
										flexGrow: 1,
									}}
								>
									<Flex gap="sm">
										<Text fw="bold">Do formularza zapytania</Text>
										<SlArrowRight size={12} />
									</Flex>
								</Button>
							</Tooltip>
						)}
						{!isProductStep && (
							<Button
								color="blue.8"
								onClick={() => setStep('product')}
								style={{
									flexGrow: 1,
								}}
							>
								<Flex gap="sm">
									<SlArrowLeft size={18} />
									<Text fw="bold">Wróć</Text>
								</Flex>
							</Button>
						)}
						{!isProductStep && (
							<Button
								type="submit"
								style={{
									flexGrow: 1,
								}}
							>
								<Flex gap="sm">
									<Text fw="bold">Wyślij zapytanie</Text>
									<SlEnvolope size={20} />
								</Flex>
							</Button>
						)}
					</Flex>

					<ResetButton handleReset={handleReset}>
						<Button variant="outline" color="red" className="flex-grow-1">
							<Text fw="bold" c="red">
								Resetuj formularz
							</Text>
						</Button>
					</ResetButton>
				</Card>
			</Box>
		</Flex>
	);
};
